import { includes } from 'lodash-es';
import type { Entry } from 'type-fest';

import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { BASIC, parse } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import type { OwnedAccount } from '@hotjar/account';

import type { OrganizationDto } from '@hotjar/organization';
import * as UserData from '../user-data';
import { getLocalOverride } from './local-override';
import { get as getSettings } from '../settings';

import type { Feature } from './types';
import type { Limits } from '../settings/types';

interface EntityWithFeatures {
  features?: Array<Feature>;
}

interface BaseSite extends EntityWithFeatures {
  account: EntityWithFeatures;
  organization: EntityWithFeatures;
}

export interface DtoSite extends BaseSite {
  // eslint-disable-next-line camelcase
  effective_plan: FeatureGroup | null;
  // eslint-disable-next-line camelcase
  effective_plan_for_feedback: FeatureGroup | null;
  effectivePlan?: never;
  effectiveAskPlan?: never;

  sessionCaptureConsoleConsent?: boolean;
  id?: number;
  trial?: {
    active: boolean;
  };
}

export interface ModelSite extends BaseSite {
  effectivePlan: FeatureGroup | null;
  effectiveAskPlan: FeatureGroup | null;
  // eslint-disable-next-line camelcase
  effective_plan?: never;
  // eslint-disable-next-line camelcase
  effective_plan_for_feedback?: never;

  sessionCaptureConsoleConsent?: boolean;
  id?: number;
  trial?: {
    active: boolean;
  };
}

export type Site = DtoSite | ModelSite;

/**
 * Check if a feature can be used by a specific site.
 * @deprecated import from @hotjar/feature-flags instead
 */
export function isSiteAllowed(feature: Feature, site: Site | null): boolean {
  // We might not have a site available. E.g. User is not logged in.
  if (!site) {
    return false;
  }

  return includes(site.features, feature);
}

/**
 * Check if a feature can be used by a specific organization which can possibly be under a different account.
 * @deprecated import from @hotjar/feature-flags instead
 */
export function isOrganizationAllowed(feature: Feature, organization: OrganizationDto): boolean {
  return (
    includes(organization.features, feature) || includes(organization.account_features, feature)
  );
}

/**
 * Check if a feature can be used by a specific account.
 * @deprecated import from @hotjar/feature-flags instead
 */
export function isAccountAllowed(
  feature: Feature,
  account: Pick<OwnedAccount, 'id' | 'features'> | null,
): boolean {
  if (!account) {
    return false;
  }

  return includes(account.features, feature);
}

/**
 * Returns the list of all features
 * @deprecated import from @hotjar/feature-flags instead
 */
export function getAll(site: Site | null): string[] {
  if (!site || !UserData.getLoggedIn()) {
    return [];
  }

  // Handle the case that react components may pass object with camelcased keys.
  const plan = site.effective_plan ? site.effective_plan : site.effectivePlan;

  // Return early if we don't have the plan (e.g. super outdated UserData cached in the browser).
  if (!plan) {
    return [];
  }

  const settings = getSettings();
  const planSettings = settings ? settings.plans[plan].features : [];

  return planSettings.concat(
    site.features || [],
    site.organization.features || [],
    site.account.features || [],
  );
}

/**
 * Returns the list of all features without gates
 * @deprecated import from @hotjar/feature-flags instead
 */
export function getAllWithoutGates(site: Site | null): string[] {
  if (!site || !UserData.getLoggedIn()) {
    return [];
  }

  // Handle the case that react components may pass object with camelcased keys.
  const plan = site.effective_plan ? site.effective_plan : site.effectivePlan;

  // Return early if we don't have the plan (e.g. super outdated UserData cached in the browser).
  if (!plan) {
    return [];
  }

  return [
    ...(site.features ?? []),
    ...(site.organization.features ?? []),
    ...(site.account.features ?? []),
  ];
}

/**
 * @private
 * @deprecated (extracted to @hotjar/feature-flags)
 */
function isAllowedBase(
  feature: Feature,
  site: Site | null,
  getAllFlags: (site: Site | null) => string[],
) {
  const localStorageOverride = getLocalOverride(feature);

  if (localStorageOverride !== null) {
    return localStorageOverride;
  }

  const features = getAllFlags(site);

  return includes(features, feature);
}

/**
 * Check if a feature can be used by a specific site.
 * @deprecated import from @hotjar/feature-flags instead
 */
export function isAllowed(feature: Feature, site: Site | null): boolean {
  return isAllowedBase(feature, site, getAll);
}

/**
 * Check if a feature can be used by a specific site without checking in gates
 * @deprecated import from @hotjar/feature-flags instead
 */
export function isAllowedWithoutGates(feature: Feature, site: Site | null): boolean {
  return isAllowedBase(feature, site, getAllWithoutGates);
}

/**
 * Get the minimum plan required for the given limit amount.
 * @deprecated import from @hotjar/feature-flags instead
 */
export function getMinimumFeatureGroupForLimit(
  limitKey: keyof Limits,
  limitValue: number,
): FeatureGroup {
  const settings = getSettings();

  if (!settings) {
    throw new Error('Settings not loaded yet.');
  }

  const featureGroupEntry = Object.entries(settings.plans)
    // Since its an un-ordered object, sort into ascending order as
    // defined by `tier`
    .sort(([, { tier: tier1 }], [, { tier: tier2 }]) => tier1 - tier2)
    // Find the first instance where `limitValue` fits into the limit of the
    // plan.
    .find(([, { limits }]) => {
      const limitValueInPlan = limits[limitKey] ?? null;

      // Limit is not defined in plan
      if (!limitValueInPlan) {
        return false;
      }

      return limitValueInPlan >= limitValue;
    }) as Entry<typeof settings.plans>;

  if (!featureGroupEntry) {
    return BASIC;
  }

  // The first item will be the feature group name, which is what we're
  // looking for.
  return featureGroupEntry[0];
}

/**
 * Get the minimum feature group required for the given feature, or null, if the given
 * feature is not available in any feature group.
 * @deprecated import from @hotjar/feature-flags instead
 */
export const getMinimumFeatureGroupForFeature = (featureName: string): FeatureGroup => {
  const settings = getSettings();

  if (!settings) {
    throw new Error('Settings not loaded yet.');
  }

  if (!featureName) {
    throw new Error('Missing feature name.');
  }

  const featureGroupEntry = Object.entries(settings.plans)
    // Since its an un-ordered object, sort into ascending order as
    // defined by `tier`
    .sort(([, { tier: tier1 }], [, { tier: tier2 }]) => tier1 - tier2)
    // Find the first instance where the `features` string array includes `featureName`.
    .find(([, { features }]) => features.includes(featureName)) as Entry<typeof settings.plans>;

  if (!featureGroupEntry) {
    return BASIC;
  }

  const [featureGroupName] = featureGroupEntry;

  return featureGroupName;
};

/**
 * Get the limits of a specific feature filtered by the featureGroup name
 * @param featureName
 * @param featureGroup
 * @deprecated import from @hotjar/feature-flags instead
 */
export const getLimitsByFeatureGroup = (
  featureName: keyof Limits,
  featureGroup: FeatureGroup,
): number | undefined => {
  const settings = getSettings();

  if (!settings) {
    throw new Error('Settings not loaded yet.');
  }

  if (!featureName) {
    throw new Error('Missing feature name');
  }

  if (!featureGroup) {
    throw new Error('Missing feature group');
  }

  if (!parse(featureGroup)) {
    throw new Error(`Wrong featureGroup: ${featureGroup}`);
  }

  return settings.plans[featureGroup]?.limits[featureName];
};
